import { useMemo } from "react";
import { ContentBlock, ImageGroupContentBlock, ImageQuery } from "@shared/types/content-block";

interface ResolvedImage {
    url: string;
    alt: string;
    width?: number;
    height?: number;
    source: string;
    license: string;
    attribution?: string;
    thumbnailUrl?: string;
}

interface RawImageGroup {
    layout?: "carousel" | "single";
    images: Array<{ query: string; limit?: number }>;
    caption?: string;
}

/**
 * Parse content string into ContentBlock array.
 * Extracts both resolved and unresolved image_group blocks.
 * Unresolved blocks (with queries) will be fetched dynamically by the ImageGroup component.
 */
export function parseContentToBlocks(content: string): ContentBlock[] {
    if (!content || typeof content !== "string") {
        return [];
    }

    // Combined regex to match both resolved and unresolved image_group blocks
    const COMBINED_REGEX = /```(image_group_resolved|image_group)\s*\n([\s\S]*?)```/g;

    const blocks: ContentBlock[] = [];
    let lastIndex = 0;
    let match;

    while ((match = COMBINED_REGEX.exec(content)) !== null) {
        const blockType = match[1]; // "image_group_resolved" or "image_group"
        const jsonContent = match[2].trim();

        // Add text before this match
        if (match.index > lastIndex) {
            const textBefore = content.slice(lastIndex, match.index).trim();
            if (textBefore) {
                blocks.push({
                    type: "text",
                    markdown: textBefore,
                });
            }
        }

        // Parse the JSON content
        try {
            const parsed = JSON.parse(jsonContent);
            
            if (blockType === "image_group_resolved") {
                // Already resolved - has image URLs
                if (parsed.type === "image_group" && Array.isArray(parsed.images)) {
                    const imageGroup: ImageGroupContentBlock = {
                        type: "image_group",
                        layout: parsed.layout,
                        images: parsed.images.map((img: ResolvedImage) => ({
                            url: img.url,
                            alt: img.alt || img.attribution || "Image",
                            width: img.width,
                            height: img.height,
                        })),
                        caption: parsed.caption,
                    };
                    blocks.push(imageGroup);
                }
            } else {
                // Unresolved - has queries that need to be fetched
                const rawGroup = parsed as RawImageGroup;
                if (Array.isArray(rawGroup.images)) {
                    const queries: ImageQuery[] = rawGroup.images.map((img) => ({
                        query: img.query,
                        limit: img.limit,
                    }));

                    const imageGroup: ImageGroupContentBlock = {
                        type: "image_group",
                        layout: rawGroup.layout,
                        queries, // Pass queries instead of images
                        caption: rawGroup.caption,
                    };
                    blocks.push(imageGroup);
                }
            }
        } catch (error) {
            console.warn("[parseContentToBlocks] Failed to parse image_group:", error);
            // Keep the raw block as text if parsing fails
            blocks.push({
                type: "text",
                markdown: match[0],
            });
        }

        lastIndex = match.index + match[0].length;
    }

    // Add remaining text after last match
    if (lastIndex < content.length) {
        const remainingText = content.slice(lastIndex).trim();
        if (remainingText) {
            blocks.push({
                type: "text",
                markdown: remainingText,
            });
        }
    }

    // If no blocks were created, treat entire content as text
    if (blocks.length === 0 && content.trim()) {
        blocks.push({
            type: "text",
            markdown: content,
        });
    }

    return blocks;
}

/**
 * Check if content contains any image group blocks (resolved or unresolved)
 */
export function hasResolvedImageBlocks(content: string): boolean {
    return content.includes("```image_group");
}

/**
 * Hook to parse message content into ContentBlocks
 */
export function useParseContentBlocks(content: string): ContentBlock[] {
    return useMemo(() => parseContentToBlocks(content), [content]);
}

export default useParseContentBlocks;
